import axios from 'axios';

import * as Helpers from '../lib/ACHelpers'
import { checkHealth, fetchDocument, reloadImageLibrary } from '../data/EditorActions';
import * as Constant from '../data/constant';
import { initDB, getStoreData, addData, deleteData, Stores } from './db';
import { insertImages, insertImage, getImageById, deleteImageById } from './image-caching';
import ThumbnailGenerationService from './ThumbnailGenerationService';

const GRID_DOCUMENTS = 'GRID_DOCUMENTS';
const GRID_ELEMENTS = 'GRID_ELEMENTS';
const GRID_PAGES = 'GRID_PAGES';
const SAVING_GRID_DOCUMENT_ID = 'SAVING_GRID_DOCUMENT_ID';
const SAVING_ERRORS = 'SAVING_ERRORS';
const LAST_NEW_IMAGE_ID = 'LAST_NEW_IMAGE_ID';
const UPLOAD_IMAGES = 'UPLOAD_IMAGES';

export default class OfflineSaveService {

  constructor() {
    this.props = undefined;
    this.isSaving = false;
    this.isSavingCurrentDocument = false;
    this.generateThumbnailForDocIds = new Set();
    this.errors = new Map();
    this.shouldNotShowAlertMsg = new Map();
  }

  static getInstance() {
    if (!OfflineSaveService.instance) {
      OfflineSaveService.instance = new OfflineSaveService();
    }
    return OfflineSaveService.instance;
  }

  async shouldSaveGridDocumentId(gridDocumentId) {
    sessionStorage.setItem(SAVING_GRID_DOCUMENT_ID, JSON.stringify(gridDocumentId));

    // Wait for save
    while (sessionStorage.getItem(SAVING_GRID_DOCUMENT_ID)) {
      await Helpers.delay(1000);
    }

    let result = undefined;
    if (sessionStorage.getItem(SAVING_ERRORS)) {
      const errors = new Map(JSON.parse(sessionStorage.getItem(SAVING_ERRORS)));
      const error = errors.get(gridDocumentId);
      if (error && error.messages.length > 0) {
        result = [...error.messages][0];
      }
      sessionStorage.removeItem(SAVING_ERRORS);
    }
    return result;
  }

  updateStorageItem(itemKey, mapKey, value) {
    let data = Helpers.getStorageItem(itemKey);
    data.set(mapKey, value);
    Helpers.setStorageItem(itemKey, data);
  }

  persistDocumentName(gridDocumentId, name) {
    const userId = sessionStorage.getItem('uid') ? parseInt(sessionStorage.getItem('uid'), 10) : undefined;
    let docChanges = Helpers.getStorageItem(GRID_DOCUMENTS);
    docChanges.set(gridDocumentId, {
      gridDocumentId,
      name,
      userId,
      dirty: true,
      modified: Date.now(),
      saveAttempted: undefined,
      failedAttempts: 0
    });
    Helpers.setStorageItem(GRID_DOCUMENTS, docChanges);
  }

  persistElement(gridElement) {
    const userId = sessionStorage.getItem('uid') ? parseInt(sessionStorage.getItem('uid'), 10) : undefined;
    let elementChanges = Helpers.getStorageItem(GRID_ELEMENTS);
    elementChanges.set(gridElement.gridElementId, {
      gridElement,
      userId,
      dirty: true,
      modified: Date.now(),
      saveAttempted: undefined,
      failedAttempts: 0
    });
    Helpers.setStorageItem(GRID_ELEMENTS, elementChanges);
  }

  persistPage(gridDocumentPage) {
    const userId = sessionStorage.getItem('uid') ? parseInt(sessionStorage.getItem('uid'), 10) : undefined;
    let pageChanges = Helpers.getStorageItem(GRID_PAGES);
    pageChanges.set(gridDocumentPage.gridDocumentPageId, {
      gridDocumentPage,
      userId,
      dirty: true,
      modified: Date.now(),
      saveAttempted: undefined,
      failedAttempts: 0
    });
    Helpers.setStorageItem(GRID_PAGES, pageChanges);
  }

  getLastNewImageId() {
    return localStorage.getItem(LAST_NEW_IMAGE_ID) ? parseInt(localStorage.getItem(LAST_NEW_IMAGE_ID), 10) : 0;
  }

  setLastNewImageId(value) {
    localStorage.setItem(LAST_NEW_IMAGE_ID, value);
  }

  async persistImage(formData) {
    let imageId = formData.get('newImageId') ? parseInt(formData.get('newImageId'), 10) : 0;

    const image = formData.get('image');
    const dataUrl = await Helpers.convertImageToBase64(image);

    const id = await addData(Stores.Images, { id: imageId, dataUrl });
    if (id) {
      imageId = id;
      insertImage({ id, dataUrl });
    }

    const imageObj = {
      imageId,
      name: image.name,
      userId: sessionStorage.getItem('uid') ? parseInt(sessionStorage.getItem('uid'), 10) : undefined,
      created: new Date(),
      modified: new Date(),
      saveAttempted: undefined,
      failedAttempts: 0
    };

    let uploadImages = Helpers.getStorageItem(UPLOAD_IMAGES);
    uploadImages.set(imageId, imageObj);
    Helpers.setStorageItem(UPLOAD_IMAGES, uploadImages);

    this.setLastNewImageId(imageId);

    return {
      ...imageObj,
      extension: imageObj.name ? imageObj.name.split('.').pop() : undefined,
      ownerId: imageObj.userId,
      deleted: undefined,
      month: undefined,
      year: undefined
    };
  }

  mergeGridDocument(gridDocument) {
    const userId = sessionStorage.getItem('uid') ? parseInt(sessionStorage.getItem('uid'), 10) : undefined;
    this.mergePrimaryGridDocument(gridDocument, userId);
    this.mergeGridElements(gridDocument, userId);
  }

  mergePrimaryGridDocument(gridDocument, userId) {
    let docChanges = Helpers.getStorageItem(GRID_DOCUMENTS);
    let docInStorage = [...docChanges.values()]
      .find(doc => doc.gridDocumentId === gridDocument.gridDocumentId && doc.userId === userId);
    if (docInStorage) {
      const modifiedInDb = (new Date(gridDocument.modified + ' GMT')).getTime();
      if (modifiedInDb > docInStorage.modified) {
        docInStorage.name = gridDocument.name;
        docInStorage.userId = userId;
        docInStorage.dirty = false;
        docInStorage.modified = modifiedInDb;
        docInStorage.saveAttempted = undefined;
        docInStorage.failedAttempts = 0;

        docChanges.set(gridDocument.gridDocumentId, docInStorage);
        Helpers.setStorageItem(GRID_DOCUMENTS, docChanges);
      } else if (docInStorage.dirty && modifiedInDb < docInStorage.modified) {
        gridDocument.name = docInStorage.name;
      }
    }
  }

  mergeGridElements(gridDocument, userId) {
    let elementChanges = Helpers.getStorageItem(GRID_ELEMENTS);
    let elementsInStorage = [...elementChanges.values()]
      .filter(element => element.gridElement.gridDocumentId === gridDocument.gridDocumentId && element.userId === userId);
    for (let elementInStorage of elementsInStorage) {
      let gridElement = gridDocument.GridElement.find(element => element.gridElementId === elementInStorage.gridElement.gridElementId);
      if (!gridElement) continue;

      const modifiedInDb = (new Date(gridElement.modified + ' GMT')).getTime();
      if (modifiedInDb > elementInStorage.modified) {
        elementInStorage.gridElement = gridElement;
        elementInStorage.userId = userId;
        elementInStorage.dirty = false;
        elementInStorage.modified = modifiedInDb;
        elementInStorage.saveAttempted = undefined;
        elementInStorage.failedAttempts = 0;

        elementChanges.set(gridElement.gridElementId, elementInStorage);
        Helpers.setStorageItem(GRID_ELEMENTS, elementChanges);
      } else if (elementInStorage.dirty && modifiedInDb < elementInStorage.modified) {
        gridElement.update(elementInStorage.gridElement);
      }
    }
  }

  mergeGridDocumentPages(gridDocument, userId) {
    let pageChanges = Helpers.getStorageItem(GRID_PAGES);
    let pagesInStorage = [...pageChanges.values()]
      .filter(page => page.gridDocumentPage.gridDocumentId === gridDocument.gridDocumentId && page.userId === userId);
    for (let pageInStorage of pagesInStorage) {
      let gridDocumentPage = gridDocument.GridDocumentPage.find(page => page.gridDocumentPageId === pageInStorage.gridDocumentPage.gridDocumentPageId);
      if (!gridDocumentPage) continue;

      const modifiedInDb = (new Date(gridDocumentPage.modified + ' GMT')).getTime();
      if (modifiedInDb > pageInStorage.modified) {
        pageInStorage.gridDocumentPage = gridDocumentPage;
        pageInStorage.userId = userId;
        pageInStorage.dirty = false;
        pageInStorage.modified = modifiedInDb;
        pageInStorage.saveAttempted = undefined;
        pageInStorage.failedAttempts = 0;

        pageChanges.set(gridDocumentPage.gridDocumentPageId, pageInStorage);
        Helpers.setStorageItem(GRID_PAGES, pageChanges);
      } else if (pageInStorage.dirty && modifiedInDb < pageInStorage.modified) {
        gridDocumentPage.update(pageInStorage.gridDocumentPage);
      }
    }
  }

  setCurrentGridDocumentId(gridDocumentId) {
    if (this.props && this.props.doc && !this.props.doc.gridDocumentId) {
      this.props.doc.gridDocumentId = gridDocumentId;
    }
  }

  async startSaveProcess(props) {
    this.props = props;

    // Initialize Indexed DB
    const status = await initDB();
    if (!status) {
      console.log('Indexed DB: Database Not Initialized');
    }

    // cache images
    const images = await getStoreData(Stores.Images);
    if (images) {
      insertImages(images);
    }

    this.cleanData();

    if (this.savingInterval) {
      window.clearInterval(this.savingInterval);
    }

    this.savingInterval = window.setInterval(async () => {
      if (this.props.currentUser && this.props.currentUser.userId) {
        await this.save();
      }
    }, Constant.OFFLINE_SAVE_INTERVAL);
  }

  async save() {
    if (this.isSaving && (this.isSavingCurrentDocument || !sessionStorage.getItem(SAVING_GRID_DOCUMENT_ID))) {
      return;
    }

    this.errors = new Map();
    const userId = this.props.currentUser.userId;
    const savingDocumentId = sessionStorage.getItem(SAVING_GRID_DOCUMENT_ID) ? parseInt(sessionStorage.getItem(SAVING_GRID_DOCUMENT_ID), 10) : undefined;
    this.isSavingCurrentDocument = savingDocumentId ? true : false;
    this.isSaving = true;

    const savingImages = [...Helpers.getStorageItem(UPLOAD_IMAGES).values()].filter(image => image.userId === userId);
    const savingDocs = [...Helpers.getStorageItem(GRID_DOCUMENTS).values()].filter(doc => doc.dirty && doc.userId === userId);
    const savingElements = [...Helpers.getStorageItem(GRID_ELEMENTS).values()].filter(element => element.dirty && element.userId === userId);
    const savingPages = [...Helpers.getStorageItem(GRID_PAGES).values()].filter(page => page.dirty && page.userId === userId);
    if (savingImages.length > 0 || savingDocs.length > 0 || savingElements.length > 0 || savingPages.length > 0) {
      await checkHealth().then(async () => {
        await this.saveImages(savingImages);
        await this.saveGridDocuments(userId, savingDocumentId);
        await this.createRevisions();
        await this.fetchCurrentDocument();

        for (const gridDocumentId of this.generateThumbnailForDocIds) {
          ThumbnailGenerationService.getInstance().add(gridDocumentId, this.props.currentUser.userId);
        }

        sessionStorage.setItem(SAVING_ERRORS, JSON.stringify(Array.from(this.errors.entries())));
      }).catch((error) => {
        if (savingDocumentId) {
          this.addError(savingDocumentId, undefined, 'Network Error');
          sessionStorage.setItem(SAVING_ERRORS, JSON.stringify(Array.from(this.errors.entries())));
        }
      });
    }

    this.isSaving = false;
    this.isSavingCurrentDocument = false;
    this.generateThumbnailForDocIds.clear();
    sessionStorage.removeItem(SAVING_GRID_DOCUMENT_ID);
  }

  async saveImages(savingImages) {
    if (savingImages.length > 0) {
      const currentDate = Date.now();

      let promises = [];
      for (let image of savingImages) {
        if ((image.failedAttempts < 2)
          || (image.failedAttempts < 5 && (((currentDate - image.saveAttempted) / 1000) > 5))
          || (image.failedAttempts > 4 && (((currentDate - image.saveAttempted) / 1000) > 30))
        ) {
          promises.push(this.saveImage(image));
        }
      }
      await Promise.all(promises);

      if (this.props.imageLibrary.isShown) {
        this.props.dispatch(reloadImageLibrary());
      }
    }
  }

  async saveGridDocuments(userId, savingDocumentId) {
    let docChanges = Helpers.getStorageItem(GRID_DOCUMENTS);
    let elementChanges = Helpers.getStorageItem(GRID_ELEMENTS);
    let pageChanges = Helpers.getStorageItem(GRID_PAGES);
    let savingDocs = [], savingElements = [], savingPages = [];
    if (savingDocumentId) {
      savingDocs = [...docChanges.values()].filter(doc => doc.dirty && doc.userId === userId && doc.gridDocumentId === savingDocumentId);
      savingElements = [...elementChanges.values()].filter(element => element.dirty && element.userId === userId && element.gridElement.gridDocumentId === savingDocumentId);
      savingPages = [...pageChanges.values()].filter(page => page.dirty && page.userId === userId && page.gridDocumentPage.gridDocumentId === savingDocumentId);
    } else {
      savingDocs = [...docChanges.values()].filter(doc => doc.dirty && doc.userId === userId);
      savingElements = [...elementChanges.values()].filter(element => element.dirty && element.userId === userId);
      savingPages = [...pageChanges.values()].filter(page => page.dirty && page.userId === userId);
    }

    let promises = [];
    if (savingDocs.length > 0) {
      promises.push(this.savePrimaryGridDocuments(savingDocs, savingDocumentId));
    }
    if (savingElements.length > 0) {
      promises.push(this.saveGridElements(savingElements, savingDocumentId));
    }
    if (savingPages.length > 0) {
      promises.push(this.saveGridDocumentPages(savingPages, savingDocumentId));
    }

    if (promises.length > 0) {
      await Promise.all(promises);
    }
  }

  async savePrimaryGridDocuments(savingDocs, savingDocumentId) {
    const currentDate = Date.now();

    let promises = [];
    for (let doc of savingDocs) {
      if ((doc.gridDocumentId === savingDocumentId)
        || (this.props.doc && this.props.doc.gridDocumentId === doc.gridDocumentId)
        || (doc.failedAttempts < 2)
        || (doc.failedAttempts < 5 && (((currentDate - doc.saveAttempted) / 1000) > 5))
        || (doc.failedAttempts > 4 && (((currentDate - doc.saveAttempted) / 1000) > 30))
      ) {
        promises.push(this.saveGridDocument(doc));
      }
    }
    await Promise.all(promises);
  }

  async saveGridElements(savingElements, savingDocumentId) {
    const currentDate = Date.now();
    let promises = [];
    for (let element of savingElements) {
      if ((element.gridElement.gridDocumentId === savingDocumentId) || (element.failedAttempts < 2)
        || (element.failedAttempts < 5 && (((currentDate - element.saveAttempted) / 1000) > 5))
        || (element.failedAttempts > 4 && (((currentDate - element.saveAttempted) / 1000) > 30))
      ) {
        promises.push(this.saveGridElement(element));
      }
    }
    await Promise.all(promises);
  }

  async saveGridDocumentPages(savingPages, savingDocumentId) {
    const currentDate = Date.now();
    let promises = [];
    for (let page of savingPages) {
      if ((page.gridDocumentPage.gridDocumentId === savingDocumentId) || (page.failedAttempts < 2)
        || (page.failedAttempts < 5 && (((currentDate - page.saveAttempted) / 1000) > 5))
        || (page.failedAttempts > 4 && (((currentDate - page.saveAttempted) / 1000) > 30))
      ) {
        promises.push(this.saveGridDocumentPage(page));
      }
    }
    await Promise.all(promises);
  }

  async saveImage(imageObj) {
    const dataUrl = getImageById(imageObj.imageId);
    if (!dataUrl) return;

    const config = {
      headers: {
        'content-type': 'multipart/form-data'
      }
    }

    const fileName = `${Date.now()}.${imageObj.name.split('.').pop()}`;
    let formData = new FormData();
    formData.append('image', Helpers.convertBase64ToBlob(dataUrl), fileName);
    formData.append('name', fileName);

    const endpoint = '/images';
    return await axios.post(endpoint, formData, config).then(async (response) => {
      if (response.data && response.data.result) {
        const savedImageId = response.data.result.image.imageId;
        this.updateBgImageForStorage(imageObj.imageId, savedImageId);
        this.updateBgImageForCurrentDocument(imageObj.imageId, savedImageId);

        let uploadImages = Helpers.getStorageItem(UPLOAD_IMAGES);
        uploadImages.delete(imageObj.imageId);
        Helpers.setStorageItem(UPLOAD_IMAGES, uploadImages);

        await deleteData(Stores.Images, imageObj.imageId);
        deleteImageById(imageObj.imageId);
      } else {
        imageObj.failedAttempts = imageObj.failedAttempts + 1;
        imageObj.saveAttempted = Date.now();
        this.updateStorageItem(UPLOAD_IMAGES, imageObj.imageId, imageObj);
      }
    }).catch((error) => {
      imageObj.failedAttempts = imageObj.failedAttempts + 1;
      imageObj.saveAttempted = Date.now();
      this.updateStorageItem(UPLOAD_IMAGES, imageObj.imageId, imageObj);
      console.log(error);
    });
  }

  async saveGridDocument(gridDocument) {
    const endpoint = '/gridDocuments/' + gridDocument.gridDocumentId;
    return await axios.post(endpoint, { name: gridDocument.name }).then((response) => {
      if (response.data && response.data.result) {
        if (response.data.result.invalid) {
          gridDocument.failedAttempts = gridDocument.failedAttempts + 1;
          gridDocument.saveAttempted = Date.now();
          this.addError(gridDocument.gridDocumentId, gridDocument.name, response.data.result.invalid);

          if (this.props.doc && this.props.doc.gridDocumentId
            && this.props.doc.gridDocumentId === gridDocument.gridDocumentId
            && !sessionStorage.getItem(SAVING_GRID_DOCUMENT_ID)
            && !this.shouldNotShowAlertMsg.has(gridDocument.gridDocumentId)
          ) {
            alert(response.data.result.invalid);
            this.shouldNotShowAlertMsg.set(gridDocument.gridDocumentId, true);
          }
        } else {
          gridDocument.dirty = false;
          gridDocument.failedAttempts = 0;
          gridDocument.saveAttempted = Date.now();
          gridDocument.modified = Date.now();

          this.generateThumbnailForDocIds.add(gridDocument.gridDocumentId);
        }
      } else {
        gridDocument.failedAttempts = gridDocument.failedAttempts + 1;
        gridDocument.saveAttempted = Date.now();
      }

      this.updateStorageItem(GRID_DOCUMENTS, gridDocument.gridDocumentId, gridDocument);
    }).catch((error) => {
      gridDocument.failedAttempts = gridDocument.failedAttempts + 1;
      gridDocument.saveAttempted = Date.now();
      this.updateStorageItem(GRID_DOCUMENTS, gridDocument.gridDocumentId, gridDocument);

      this.addError(gridDocument.gridDocumentId, gridDocument.name, error);
    });
  }

  async saveGridElement(element) {
    const expiredText = 'Session expired. Please return to ActivityConnection website.';
    const persistPath = '/gridDocuments/' + element.gridElement.gridDocumentId + '/gridElements/' + element.gridElement.gridElementId;
    return await axios.post(persistPath, Object.assign({}, element.gridElement)).then((response) => {
      if (response.data && response.data.result) {
        element.gridElement = Object.assign({}, response.data.result.gridElement);
        element.dirty = false;
        element.failedAttempts = 0;
        element.saveAttempted = Date.now();
        element.modified = Date.now();

        this.generateThumbnailForDocIds.add(element.gridElement.gridDocumentId);
      } else {
        element.failedAttempts = element.failedAttempts + 1;
        element.saveAttempted = Date.now();
        if (response.data === expiredText) {
          this.addError(element.gridElement.gridDocumentId, undefined, response.data);

          if (this.props.doc && this.props.doc.gridDocumentId
            && this.props.doc.gridDocumentId === element.gridElement.gridDocumentId
            && !sessionStorage.getItem(SAVING_GRID_DOCUMENT_ID)
            && !this.shouldNotShowAlertMsg.has(element.gridElement.gridDocumentId)
          ) {
            alert(response.data.result.invalid);
            this.shouldNotShowAlertMsg.set(element.gridElement.gridDocumentId, true);
          }
        }
      }
      this.updateStorageItem(GRID_ELEMENTS, element.gridElement.gridElementId, element);
    }).catch((error) => {
      element.failedAttempts = element.failedAttempts + 1;
      element.saveAttempted = Date.now();
      this.updateStorageItem(GRID_ELEMENTS, element.gridElement.gridElementId, element);

      this.addError(element.gridElement.gridDocumentId, undefined, error);
    });
  }

  async saveGridDocumentPage(page) {
    const persistPath = '/gridDocuments/' + page.gridDocumentPage.gridDocumentId + '/gridDocumentPages/' + page.gridDocumentPage.page;
    return await axios.post(persistPath, Object.assign({}, page.gridDocumentPage)).then((response) => {
      if (response.data && response.data.result) {
        page.gridDocumentPage = Object.assign({}, response.data.result.gridDocumentPage);
        page.dirty = false;
        page.failedAttempts = 0;
        page.saveAttempted = Date.now();
        page.modified = Date.now();

        this.generateThumbnailForDocIds.add(page.gridDocumentPage.gridDocumentId);
        this.updateStorageItem(GRID_PAGES, page.gridDocumentPage.gridDocumentPageId, page);
      } else {
        page.failedAttempts = page.failedAttempts + 1;
        page.saveAttempted = Date.now();
        this.updateStorageItem(GRID_PAGES, page.gridDocumentPage.gridDocumentPageId, page);
      }
    }).catch((error) => {
      page.failedAttempts = page.failedAttempts + 1;
      page.saveAttempted = Date.now();
      this.updateStorageItem(GRID_PAGES, page.gridDocumentPage.gridDocumentPageId, page);

      this.addError(page.gridDocumentPage.gridDocumentId, undefined, error);
    });
  }

  async createRevisions() {
    let promises = [];
    for (const gridDocumentId of this.generateThumbnailForDocIds) {
      if (this.props.doc && gridDocumentId === this.props.doc.gridDocumentId) {
        // No need to create a revision for current edit calendar
      } else {
        promises.push(this.createRevision(gridDocumentId));
      }
    }

    if (promises.length > 0) {
      await Promise.all(promises);
    }
  }

  createRevision(gridDocumentId) {
    const endpoint = `/gridDocuments/${gridDocumentId}/createRevision`;
    return axios.post(endpoint, { versionName: 'Auto-Saved' })
      .then(function (response) {
      }).catch(function (error) {
        console.log(error);
      });
  }

  async fetchCurrentDocument() {
    if (this.props.doc && this.props.doc.gridDocumentId
      && this.generateThumbnailForDocIds.has(this.props.doc.gridDocumentId)) {
      await this.props.dispatch(fetchDocument(this.props.doc.gridDocumentId));
    }
  }

  updateBgImageForStorage(oldBackgroundImageId, newBackgroundImageId) {
    let elementChanges = Helpers.getStorageItem(GRID_ELEMENTS);
    elementChanges.forEach((value, key) => {
      if (value.gridElement.backgroundImageId === oldBackgroundImageId) {
        value.dirty = true;
        value.gridElement.backgroundImageId = newBackgroundImageId;
      }
    });
    Helpers.setStorageItem(GRID_ELEMENTS, elementChanges);

    let pageChanges = Helpers.getStorageItem(GRID_PAGES);
    pageChanges.forEach((value, key) => {
      if (value.gridDocumentPage.backgroundImageId === oldBackgroundImageId) {
        value.dirty = true;
        value.gridDocumentPage.backgroundImageId = newBackgroundImageId;
      }
    });
    Helpers.setStorageItem(GRID_PAGES, pageChanges);
  }

  updateBgImageForCurrentDocument(oldBackgroundImageId, newBackgroundImageId) {
    if (this.props.doc) {
      for (let element of this.props.doc.GridElement) {
        if (element.backgroundImageId === oldBackgroundImageId) {
          element.backgroundImageId = newBackgroundImageId;
        }
      }
      for (let page of this.props.doc.GridDocumentPage) {
        if (page.backgroundImageId === oldBackgroundImageId) {
          page.backgroundImageId = newBackgroundImageId;
        }
      }
    }
  }

  addError(gridDocumentId, name, message) {
    let messages = new Set();
    const error = this.errors.get(gridDocumentId);
    if (error) {
      messages = error.messages;
    }
    messages.add(message);
    this.errors.set(gridDocumentId, { name, messages });
  }

  cleanData() {
    if (this.props.doc && this.props.doc.gridDocumentId && this.props.currentUser && this.props.currentUser.userId) {
      const gridDocumentId = this.props.doc.gridDocumentId;
      const userId = this.props.currentUser.userId;
      this.cleanGridDocument(gridDocumentId, userId);
      this.cleanGridElements(gridDocumentId, userId);
      this.cleanGridDocumentPages(gridDocumentId, userId);
    }
  }

  cleanGridDocument(gridDocumentId, userId) {
    let docChanges = Helpers.getStorageItem(GRID_DOCUMENTS);
    let noDataChanges = [...docChanges.values()]
      .filter(doc => !doc.dirty && doc.gridDocumentId !== gridDocumentId && doc.userId === userId);
    if (noDataChanges && noDataChanges.length > 0) {
      for (const doc of noDataChanges) {
        docChanges.delete(doc.gridDocumentId);
      }
      Helpers.setStorageItem(GRID_DOCUMENTS, docChanges);
    }
  }

  cleanGridElements(gridDocumentId, userId) {
    let elementChanges = Helpers.getStorageItem(GRID_ELEMENTS);
    let noDataChanges = [...elementChanges.values()]
      .filter(element => !element.dirty && element.gridElement.gridDocumentId !== gridDocumentId && element.userId === userId);
    if (noDataChanges && noDataChanges.length > 0) {
      for (const element of noDataChanges) {
        elementChanges.delete(element.gridElement.gridElementId);
      }
      Helpers.setStorageItem(GRID_ELEMENTS, elementChanges);
    }
  }

  cleanGridDocumentPages(gridDocumentId, userId) {
    let pageChanges = Helpers.getStorageItem(GRID_PAGES);
    let noDataChanges = [...pageChanges.values()]
      .filter(page => !page.dirty && page.gridDocumentPage.gridDocumentId !== gridDocumentId && page.userId === userId);
    if (noDataChanges && noDataChanges.length > 0) {
      for (const page of noDataChanges) {
        pageChanges.delete(page.gridDocumentPage.gridDocumentPageId);
      }
      Helpers.setStorageItem(GRID_PAGES, pageChanges);
    }
  }

  removeGridDocument(gridDocumentId) {
    let docChanges = Helpers.getStorageItem(GRID_DOCUMENTS);
    docChanges.delete(gridDocumentId);
    Helpers.setStorageItem(GRID_DOCUMENTS, docChanges);
  }

  removeGridElement(gridElementId) {
    let elementChanges = Helpers.getStorageItem(GRID_ELEMENTS);
    elementChanges.delete(gridElementId);
    Helpers.setStorageItem(GRID_ELEMENTS, elementChanges);
  }

  removeGridDocumentPage(gridDocumentPageId) {
    let pageChanges = Helpers.getStorageItem(GRID_PAGES);
    pageChanges.delete(gridDocumentPageId);
    Helpers.setStorageItem(GRID_PAGES, pageChanges);
  }

  removeGridElementsAndPages(gridDocumentId) {
    let elementChanges = Helpers.getStorageItem(GRID_ELEMENTS);
    const elements = [...elementChanges.values()].filter(element => element.gridElement.gridDocumentId === gridDocumentId);
    if (elements && elements.length > 0) {
      for (const element of elements) {
        elementChanges.delete(element.gridElement.gridElementId);
      }
      Helpers.setStorageItem(GRID_ELEMENTS, elementChanges);
    }

    let pageChanges = Helpers.getStorageItem(GRID_PAGES);
    const pages = [...pageChanges.values()].filter(page => page.gridDocumentPage.gridDocumentId === gridDocumentId);
    if (pages && pages.length > 0) {
      for (const page of pages) {
        pageChanges.delete(page.gridDocumentPage.gridDocumentPageId);
      }
      Helpers.setStorageItem(GRID_PAGES, pageChanges);
    }
  }

  reset(gridDocumentId) {
    this.shouldNotShowAlertMsg.delete(gridDocumentId);
  }

  getSaveErrors() {
    return this.errors;
  }
}



// WEBPACK FOOTER //
// ./src/services/OfflineSaveService.js